import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import { LoadingSpinner } from '../../common/loading/LoadingSpinner';
import WF_Header from '../../common/header/WfHeader';
import WF_Map from '../../common/map/WF_Map';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import WF_MapSearchHeader from '../../common/mapSearchHeader/WF_MapSearchHeader';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import { defaultMapCenter, defaultMapZoom } from 'src/common/location/defaultMapSettings';

function WF_4_5_2_PropertiesMap() {
  const { t } = useTranslation();
  const { availableWorkplaces, setAvailableWorkplaces } = useWfWorkplaceStore();
  const { user, users } = useWfUserStore();
  const {
    salesOfficeOptions,
    sectionOptions,
    setMapCenter,
    setZoom,
    setUserOffice,
    filterParameters,
  } = useWfFilterStore();
  const { fetchWorkplaces } = useWfWorkplaceService();
  const headerRef = useRef<HTMLDivElement>(null);
  const searchHeaderRef = useRef<HTMLDivElement>(null);
  const [mapContainerHeight, setMapContainerHeight] = useState('100%');

  const [isSearched, setIsSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTimeout, setIsTimeout] = useState(false);
  useEffect(() => {
    const hasMandatoryFilters =
      Boolean(filterParameters.branch) && Boolean(filterParameters.salesOffice);
    const hasCustomerFilter = Boolean(filterParameters.customerNumber);

    if ((hasMandatoryFilters || hasCustomerFilter) && !isSearched) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterParameters.branch,
    filterParameters.salesOffice,
    filterParameters.customerNumber,
    isSearched,
  ]);
  const [recenterTrigger, setRecenterTrigger] = useState(0);

  const GAP_HEIGHT = 8;

  useEffect(() => {
    const calculateMapHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      const searchHeaderHeight = searchHeaderRef.current?.offsetHeight ?? 0;
      const height = `calc(130vh - ${headerHeight}px - ${searchHeaderHeight}px - ${GAP_HEIGHT}px)`;
      setMapContainerHeight(height);
    };

    calculateMapHeight();
    window.addEventListener('resize', calculateMapHeight);

    return () => {
      window.removeEventListener('resize', calculateMapHeight);
    };
  }, []);

  useEffect(() => {
    /**
     * Update map center location. In desktop "Map" screen, should follow following prority:
     * 1. Initially, Center on user's location
     * 2. Searched with Customer -> Center on Customer Location
     * 3. If in above cases, if the location data is not available, center on default location
     */
    const salesOfficeCode = filterParameters.salesOffice;
    const sectionCode = filterParameters.section;
    const salesOffice = salesOfficeOptions.find((office) => office.code === salesOfficeCode);
    const section = sectionOptions.find((section) => section.code === sectionCode);

    // Case 1
    const hasCustomerFilter = Boolean(filterParameters.customerNumber);
    if (!hasCustomerFilter) {
      const userLocation = user?.location;
      if (userLocation?.latitude != null && userLocation?.longitude != null) {
        setMapCenter(userLocation);
        setZoom(defaultMapZoom);
        setUserOffice(section?.location ? section : (salesOffice ?? null));
        return;
      }
    }

    // Case 2
    const customerLocation = availableWorkplaces.find(
      (workplace) => workplace.customerId === filterParameters.customerNumber?.trim()
    )?.location;
    if (customerLocation) {
      setMapCenter(customerLocation);
      setZoom(defaultMapZoom);
      setUserOffice(null);
      return;
    }

    // Case 3
    setMapCenter(defaultMapCenter);
    setZoom(defaultMapZoom);
    setUserOffice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.location, setMapCenter, availableWorkplaces, setZoom]);

  const handleSearch = async () => {
    setIsLoading(true);
    setIsTimeout(false);
    setAvailableWorkplaces([]);
    try {
      await fetchWorkplaces();
      setIsSearched(true);
      setRecenterTrigger((prev) => prev + 1);
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        setIsTimeout(true);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-100">
      <div ref={headerRef}>
        <WF_Header />
      </div>
      <div ref={searchHeaderRef}>
        <WF_MapSearchHeader onSearch={handleSearch} showNoResults={!isTimeout} />
      </div>
      {isTimeout && (
        <h3 className="mx-2 mb-2 text-center text-xl">{t('wf.common.searchTimeout')}</h3>
      )}
      {isLoading && (
        <div className="fixed inset-0 z-[9999]">
          <LoadingSpinner />
        </div>
      )}
      <div style={{ height: `${GAP_HEIGHT}px` }} />
      <div style={{ height: mapContainerHeight }}>
        <WF_Map
          workplaces={isSearched ? availableWorkplaces : []}
          workers={users}
          user={user}
          legendButtonVisible
          className="size-full"
          recenterTrigger={recenterTrigger}
        />
      </div>
    </div>
  );
}

export default WF_4_5_2_PropertiesMap;
